import { useState } from 'react';
import type { PriceSummary } from '@hotjar/webapp/billing/subscription';
import { trackClick } from '@hotjar/webapp/tracking/common';
import { useOwnedAccount } from '@hotjar/account';
import { extractAccountGroupingProps } from '@hotjar/webapp/tracking/utils';
import { createGlobalModal } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { useSites } from '@hotjar/site';
import { useProductCatalog } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';
import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import type { CostSpecDTO } from '@hotjar/webapp/domains/billing';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { getChangesSummary } from './SummaryOfChanges/utils';
import { SummaryOfChanges } from './SummaryOfChanges/SummaryOfChanges';
import { MODAL_NAME } from './types';
import type { HighestPlans } from './components/MultiSkuChurnDeflectionModal';
import { MultiSkuChurnDeflectionModal } from './components/MultiSkuChurnDeflectionModal';
import { ChurnSurveyModal } from './ChurnSurveyModal/ChurnSurveyModal';
import type { ChangeListItem } from './SummaryOfChanges/types';

interface Props {
  payNowPrice?: PriceSummary;
  payLaterPrice?: PriceSummary;
  costSpecs: CostSpecDTO[];
}

/**
 * Gets the highest plans the user is downgrading.
 * The user may want to downgrade multiple sites at once.
 */
export function getHighestPlans(changeList: ChangeListItem[]): HighestPlans {
  const sortedPlans = new Map([
    ['basic', 1],
    ['plus', 2],
    ['business', 3],
    ['scale', 4],
  ]) as Map<FeatureGroup, number>;

  return changeList.reduce(
    (highestPlans, changeItem) => {
      changeItem.currentPlans.forEach((plan) => {
        const currentPlanRank = sortedPlans.get(plan.type) ?? 0;
        const highestPlanRank = sortedPlans.get(highestPlans[plan.productType]) ?? 0;
        if (currentPlanRank > highestPlanRank) {
          // eslint-disable-next-line no-param-reassign
          highestPlans[plan.productType] = plan.type;
        }
      });
      return highestPlans;
    },
    {
      [ProductType.Analytics]: 'basic',
      [ProductType.Feedback]: 'basic',
    } as { [key in ProductType]: FeatureGroup },
  );
}

export function SummaryOfChangesWrapper({
  onCancel,
  onSubmit,
  payNowPrice,
  payLaterPrice,
  costSpecs,
}: Props & GlobalModalProps<void>): JSX.Element | null {
  const { data: account } = useOwnedAccount();
  const {
    query: { data: sites },
  } = useSites();
  const { data: productCatalog } = useProductCatalog();
  const isChurning = payLaterPrice?.total?.eq('0');

  const [showChurnSurvey, setShowChurnSurvey] = useState<boolean>(false);

  if (!account) {
    return null;
  }

  const changeList = getChangesSummary({ productCatalog, costSpecs, sites });

  if (!changeList) {
    return null;
  }

  if (isChurning && showChurnSurvey) {
    return (
      <ChurnSurveyModal
        onSubmit={onSubmit}
        onCancel={() => {
          onCancel(new Error('Cancelled'));
        }}
        changeList={changeList}
        account={account}
      />
    );
  }

  if (isChurning && !showChurnSurvey) {
    return (
      <MultiSkuChurnDeflectionModal
        onSubmit={() => {
          setShowChurnSurvey(true);
        }}
        onCancel={() => {
          onCancel(new Error('Cancelled'));
        }}
        highestPlans={getHighestPlans(changeList)}
      />
    );
  }

  return (
    <SummaryOfChanges
      onSubmit={onSubmit}
      onCancel={() => {
        onCancel(new Error('Cancelled'));
      }}
      payLaterPrice={payLaterPrice}
      payNowPrice={payNowPrice}
      onPopoverClicked={(uiPosition: string) => {
        trackClick({
          name: `${MODAL_NAME.planChange} popover`,
          uiPosition,
          groupData: extractAccountGroupingProps(account),
        });
      }}
      changeList={changeList}
      account={account}
    />
  );
}

export const openSummaryOfChangesModal = createGlobalModal<Props, void, Error>(
  SummaryOfChangesWrapper,
);
